import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Lock,
  Loader2,
  RefreshCw,
  DollarSign,
  Mail,
  ListChecks,
  Globe,
  TrendingUp,
  ShoppingCart,
  Clock,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Send,
  AlertTriangle,
  Radio,
  Gift,
  Download,
  FileText,
  Presentation,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtTimeAgo = (iso) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const STATUS_TONE = {
  sent: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  scheduled: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  failed_permanent: 'text-red-400 bg-red-500/10 border-red-500/30',
  draft: 'text-muted-foreground bg-muted/20 border-muted/30',
  posted: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
};

const KPICard = ({ icon: Icon, label, value, delta, deltaLabel, tint = 'secondary', testid }) => (
  <Card
    className="p-5 bg-card border-silver/20 hover:border-secondary/40 transition-colors"
    data-testid={testid}
  >
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg bg-${tint}/15 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${tint}`} />
      </div>
      {delta != null && (
        <Badge variant="outline" className="text-[10px] border-secondary/30 text-secondary">
          +{delta} {deltaLabel}
        </Badge>
      )}
    </div>
    <div className="text-3xl font-heading text-foreground leading-none mb-1.5" data-testid={`${testid}-value`}>
      {value}
    </div>
    <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
      {label}
    </div>
  </Card>
);

const Section = ({ icon: Icon, title, subtitle, children, testid }) => (
  <Card className="p-6 bg-card border-silver/20" data-testid={testid}>
    <div className="flex items-start gap-3 mb-5">
      <Icon className="w-5 h-5 text-secondary mt-0.5" />
      <div>
        <h2 className="font-heading text-lg text-foreground">{title}</h2>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {children}
  </Card>
);

function DailyBriefingToggle({ token }) {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/assistant/briefing-config`, {
        headers: { 'X-Admin-Token': token },
      });
      setConfig(data);
    } catch {
      // silent
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (nextEnabled) => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/assistant/briefing-config`,
        { ...config, enabled: nextEnabled },
        { headers: { 'X-Admin-Token': token } },
      );
      toast.success(nextEnabled ? 'Daily briefing email turned ON' : 'Daily briefing email turned OFF');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update setting.');
    } finally {
      setSaving(false);
    }
  };

  if (!config) return null;
  const enabled = !!config.enabled;

  return (
    <Card
      className={`p-4 flex flex-wrap items-center justify-between gap-3 ${enabled ? 'border-secondary/40 bg-secondary/5' : 'border-silver/30 bg-card'}`}
      data-testid="daily-briefing-toggle-card"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${enabled ? 'bg-secondary/20 text-secondary' : 'bg-muted text-muted-foreground'}`}>
          <Mail className="w-5 h-5" />
        </div>
        <div>
          <p className="font-heading text-base text-foreground leading-tight">
            PB Daily Post Email
            <Badge className={`ml-2 text-[10px] ${enabled ? 'bg-secondary/20 text-secondary border-secondary/30' : 'bg-muted text-muted-foreground border-silver/30'}`}>
              {enabled ? 'ON' : 'OFF'}
            </Badge>
          </p>
          <p className="text-xs text-muted-foreground">
            {enabled
              ? `Sends a "today\u2019s post" email to ${config.recipient_email} at ${config.hour_mst}am MST daily.`
              : 'Automated daily post email is turned off. You won\u2019t receive any more of these.'}
          </p>
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => toggle(!enabled)}
        disabled={saving}
        className={enabled
          ? 'bg-maroon text-primary-foreground hover:bg-maroon/90'
          : 'bg-secondary text-secondary-foreground hover:bg-secondary/90'}
        data-testid="daily-briefing-toggle-button"
      >
        {saving ? (
          <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving...</>
        ) : (
          enabled ? 'Turn OFF' : 'Turn ON'
        )}
      </Button>
    </Card>
  );
}

function GiftAccessSection({ token, onSent }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [product, setProduct] = useState('ebook');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [recentGifts, setRecentGifts] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/gifts?limit=15`, {
        headers: { 'X-Admin-Token': token },
      });
      setRecentGifts(data.gifts || []);
    } catch {
      // silent
    }
  }, [token]);

  const send = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/gift`,
        {
          email: email.trim().toLowerCase(),
          product,
          recipient_name: name.trim() || null,
          gifter_note: note.trim() || null,
          origin_url: window.location.origin,
        },
        { headers: { 'X-Admin-Token': token } },
      );
      if (data.email_sent) {
        toast.success(`Gift sent to ${email} 🎁`);
      } else {
        toast.warning(`Gift created — but the confirmation email failed. Share this link manually: ${data.claim_url}`);
      }
      setEmail(''); setName(''); setNote('');
      if (showHistory) loadHistory();
      onSent && onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create gift');
    } finally {
      setSending(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url).then(
      () => toast.success('Link copied'),
      () => toast.error('Copy failed — select and copy manually'),
    );
  };

  return (
    <Card className="p-6 bg-card border-secondary/30 mb-6" data-testid="section-gift-access">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-start gap-3">
          <Gift className="w-5 h-5 text-secondary mt-0.5" />
          <div>
            <h2 className="font-heading text-lg text-foreground">Gift Access</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Comp a free copy of the E-Book, Practice App, or Power Collective. Recipient gets a magic-link email instantly.
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-xs"
          onClick={() => { setShowHistory((s) => !s); if (!showHistory) loadHistory(); }}
          data-testid="gift-toggle-history"
        >
          {showHistory ? 'Hide history' : 'Show recent gifts'}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold block mb-1">Recipient email *</label>
          <Input
            type="email"
            placeholder="friend@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-card/40 border-silver/30"
            data-testid="gift-email-input"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold block mb-1">Recipient first name (optional)</label>
          <Input
            type="text"
            placeholder="e.g. Jane"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-card/40 border-silver/30"
            data-testid="gift-name-input"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-2 mb-3" data-testid="gift-product-picker">
        {[
          { id: 'ebook', label: 'E-Book', sub: '$19.99 value' },
          { id: 'practice', label: 'Practice App', sub: '$9.99 value' },
          { id: 'collective', label: 'Power Collective', sub: '$48.50/mo' },
        ].map((p) => {
          const active = product === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setProduct(p.id)}
              className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
                active ? 'bg-secondary/15 border-secondary text-secondary' : 'bg-card/40 border-silver/20 hover:bg-card/70 text-foreground'
              }`}
              data-testid={`gift-product-${p.id}`}
            >
              <p className="text-sm font-semibold">{p.label}</p>
              <p className="text-[11px] text-muted-foreground">{p.sub}</p>
            </button>
          );
        })}
      </div>

      <div className="mb-3">
        <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold block mb-1">Personal note (optional)</label>
        <Input
          type="text"
          placeholder="e.g. For all the support you've given me 💛"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={500}
          className="bg-card/40 border-silver/30"
          data-testid="gift-note-input"
        />
      </div>

      <Button
        onClick={send}
        disabled={sending || !email}
        className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
        data-testid="gift-send-button"
      >
        {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Gift className="w-4 h-4 mr-2" /> Send gift &amp; email recipient</>}
      </Button>

      {showHistory && (
        <div className="mt-6 border-t border-silver/10 pt-4" data-testid="gift-history-list">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">Recent gifts</p>
          {recentGifts.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No gifts sent yet.</p>
          ) : (
            <ul className="space-y-2">
              {recentGifts.map((g) => (
                <li key={g.id} className="flex items-start justify-between gap-3 text-xs rounded-lg bg-card/30 border border-silver/10 px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground">
                      <Badge variant="outline" className="capitalize text-[10px] mr-2 border-secondary/30 text-secondary">{g.product}</Badge>
                      {g.email}
                      {g.claimed_at && <Badge className="ml-2 text-[10px] bg-emerald-500/15 text-emerald-400 border-emerald-500/30">claimed</Badge>}
                    </p>
                    {g.gifter_note && <p className="text-muted-foreground/70 italic mt-1 line-clamp-1">&ldquo;{g.gifter_note}&rdquo;</p>}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[10px] h-7 px-2"
                    onClick={() => copyLink(g.claim_url)}
                    data-testid={`gift-copy-${g.id}`}
                  >
                    Copy link
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

function CartRecoveryBlastBlock({ token, onSent }) {
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const fetchPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/cart-recovery-blast`,
        { dry_run: true },
        { headers: { 'X-Admin-Token': token } },
      );
      setPreview(data);
      setConfirming(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not preview blast');
    } finally {
      setPreviewing(false);
    }
  };

  const sendBlast = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/cart-recovery-blast`,
        { dry_run: false },
        { headers: { 'X-Admin-Token': token } },
      );
      toast.success(
        `${data.queued_now} recovery email${data.queued_now === 1 ? '' : 's'} queued · code ${data.promo_code} · $${data.discounted_amount_usd.toFixed(2)}`,
      );
      setConfirming(false);
      setPreview(null);
      onSent?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  if (!confirming || !preview) {
    return (
      <div className="rounded-lg border border-maroon/30 bg-maroon/5 p-4">
        <p className="text-xs uppercase tracking-widest text-secondary font-semibold mb-2 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> Final-push recovery
        </p>
        <p className="text-sm text-foreground/90 mb-3 leading-relaxed">
          Send a bigger discount (<strong>POWER8 — $8 off</strong>) to all abandoned carts with a captured email. Idempotent — clicking twice in the same day won&apos;t double-send.
        </p>
        <Button
          size="sm"
          className="w-full bg-maroon hover:bg-maroon/90 text-primary-foreground"
          onClick={fetchPreview}
          disabled={previewing}
          data-testid="cart-recovery-preview-btn"
        >
          {previewing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
          Preview Recovery Blast
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-secondary/40 bg-secondary/10 p-4" data-testid="cart-recovery-preview-block">
      <p className="text-xs uppercase tracking-widest text-secondary font-semibold mb-2 flex items-center gap-1.5">
        <AlertTriangle className="w-3.5 h-3.5" /> Confirm before sending
      </p>
      <ul className="text-sm text-foreground/90 mb-3 space-y-1">
        <li>Recipients: <strong data-testid="cart-recovery-preview-recipients">{preview.queued_now}</strong> abandoned cart(s) with email</li>
        <li>Already sent this campaign: <strong>{preview.skipped_already_in_campaign}</strong></li>
        <li>Promo: <code className="text-secondary font-bold">{preview.promo_code}</code> · ${preview.discount_off_usd.toFixed(2)} off</li>
        <li>Final price: <strong>${preview.discounted_amount_usd.toFixed(2)}</strong></li>
      </ul>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          className="flex-1"
          onClick={() => { setConfirming(false); setPreview(null); }}
          disabled={sending}
          data-testid="cart-recovery-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="flex-1 bg-secondary text-secondary-foreground hover:bg-secondary/90"
          onClick={sendBlast}
          disabled={sending || preview.queued_now === 0}
          data-testid="cart-recovery-send-btn"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
          {preview.queued_now === 0 ? 'Nothing to send' : `Send ${preview.queued_now} email${preview.queued_now === 1 ? '' : 's'}`}
        </Button>
      </div>
    </div>
  );
}

export default function LaunchDashboardPage() {
  const [token, setToken] = useState(() => localStorage.getItem('pb-admin-token') || '');
  const [tokenInput, setTokenInput] = useState('');
  const [authed, setAuthed] = useState(false);
  const [authChecking, setAuthChecking] = useState(() => Boolean(localStorage.getItem('pb-admin-token')));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = useCallback(async (t = token) => {
    if (!t) return;
    setLoading(true);
    try {
      const { data: payload } = await axios.get(`${API}/admin/launch-dashboard`, {
        headers: { 'X-Admin-Token': t },
      });
      setData(payload);
      setAuthed(true);
      localStorage.setItem('pb-admin-token', t);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthed(false);
        localStorage.removeItem('pb-admin-token');
        toast.error('Invalid admin token');
      } else {
        toast.error('Could not refresh dashboard');
      }
    } finally {
      setLoading(false);
      setAuthChecking(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    // Defer to next tick so setState inside load() doesn't fire synchronously
    // during effect execution (satisfies react-hooks/set-state-in-effect).
    const id = setTimeout(() => { load(token); }, 0);
    return () => clearTimeout(id);
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!authed || !autoRefresh) return undefined;
    const interval = setInterval(() => load(token), 30000);
    return () => clearInterval(interval);
  }, [authed, autoRefresh, token, load]);

  const submitToken = (e) => {
    e.preventDefault();
    if (tokenInput.trim()) {
      setToken(tokenInput.trim());
      load(tokenInput.trim());
    }
  };

  if (!authed) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md p-8 bg-card border-silver/20">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-secondary/15 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-secondary" />
            </div>
            <h1 className="font-heading text-2xl text-foreground mb-1">Launch Dashboard</h1>
            <p className="text-sm text-muted-foreground">
              Enter your admin token to see live launch metrics.
            </p>
          </div>
          <form onSubmit={submitToken} className="space-y-3" data-testid="launch-auth-form">
            <Input
              type="password"
              placeholder="Admin token"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              autoFocus
              data-testid="launch-token-input"
            />
            <Button
              type="submit"
              className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
              disabled={authChecking || !tokenInput.trim()}
              data-testid="launch-token-submit"
            >
              {authChecking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              Unlock Dashboard
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-secondary animate-spin" />
      </div>
    );
  }

  const { sales, email_pipeline, pb_queue, content360, cart_recovery, as_of } = data;

  return (
    <div className="min-h-screen bg-background" data-testid="launch-dashboard-root">
      <header className="border-b border-silver/15 bg-card/40 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between max-w-6xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-secondary/40 to-maroon/40 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-foreground" />
            </div>
            <div>
              <h1 className="font-heading text-xl text-foreground leading-tight">Launch Dashboard</h1>
              <p className="text-xs text-muted-foreground">
                Updated {fmtTimeAgo(as_of)} {autoRefresh && '· auto-refresh on'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setAutoRefresh((v) => !v)}
              data-testid="launch-toggle-autorefresh"
            >
              <Clock className="w-3.5 h-3.5 mr-1.5" /> Auto {autoRefresh ? 'On' : 'Off'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => load()}
              disabled={loading}
              data-testid="launch-refresh-button"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-6xl space-y-6">
        {/* PROJECT HANDOFF — for sharing with other AI agents */}
        <Card
          className="p-5 bg-card border-silver/30"
          data-testid="project-handoff-card"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-silver/20 flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-foreground" />
            </div>
            <div className="flex-1">
              <p className="font-heading text-base text-foreground leading-tight">
                Project Handoff Files
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Share with any AI (Claude, ChatGPT, Gemini) to continue building. <strong className="text-foreground">Best:</strong> download the full file and drag it into the AI&apos;s chat as an attachment.
              </p>
            </div>
          </div>

          {/* Primary: full file */}
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/project-handoff/download?token=${encodeURIComponent(token)}`}
            data-testid="download-project-handoff"
            className="block mb-3"
          >
            <Button className="w-full h-12 bg-foreground text-background hover:bg-foreground/90 font-semibold">
              <Download className="w-4 h-4 mr-2" /> Download Full Handoff (.md) &mdash; recommended
            </Button>
          </a>

          {/* Fallback: 3 chunks for AIs that don't accept file uploads */}
          <div className="pt-3 border-t border-silver/20">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold mb-2">
              If the other AI can&rsquo;t accept file uploads &mdash; download in 3 parts:
            </p>
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map((n) => (
                <a
                  key={n}
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/project-handoff/part/${n}?token=${encodeURIComponent(token)}`}
                  data-testid={`download-handoff-part-${n}`}
                >
                  <Button size="sm" variant="outline" className="w-full">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> Part {n} of 3
                  </Button>
                </a>
              ))}
            </div>
          </div>
        </Card>

        {/* DAILY BRIEFING TOGGLE — turn off the automated "today's post" email */}
        <DailyBriefingToggle token={token} />

        {/* QUICK ACTIONS (deck, prep tools) */}
        <Card className="p-4 bg-card border-secondary/30 flex flex-wrap items-center justify-between gap-3" data-testid="quick-actions-bar">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-secondary shrink-0" />
            <div>
              <p className="font-heading text-base text-foreground leading-tight">Quick Links</p>
              <p className="text-xs text-muted-foreground">Jump to your PB Assistant</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/assistant" data-testid="open-pb-assistant">
              <Button size="sm" variant="outline">
                PB Assistant
              </Button>
            </Link>
          </div>
        </Card>

        {/* KPI ROW */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4"
        >
          <KPICard
            icon={DollarSign}
            label="Revenue (total)"
            value={fmtMoney(sales.revenue_total_usd)}
            delta={sales.last_24h > 0 ? sales.last_24h : null}
            deltaLabel="sales 24h"
            tint="secondary"
            testid="kpi-revenue"
          />
          <KPICard
            icon={ShoppingCart}
            label="E-Book Sales"
            value={sales.total_paid}
            delta={sales.last_7d > 0 ? sales.last_7d : null}
            deltaLabel="this week"
            tint="primary"
            testid="kpi-sales"
          />
          <KPICard
            icon={Mail}
            label="Emails Due 24h"
            value={email_pipeline.due_next_24h}
            tint="secondary"
            testid="kpi-emails-due"
          />
        </motion.div>

        {/* GIFT ACCESS PANEL */}
        <GiftAccessSection token={token} onSent={() => load(token)} />

        {/* MAIN GRID */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* RECENT SALES */}
          <Section
            icon={ShoppingCart}
            title="Recent Sales"
            subtitle={`AOV ${fmtMoney(sales.aov_usd)} · 24h ${fmtMoney(sales.revenue_24h_usd)} from ${sales.last_24h} order(s)`}
            testid="section-sales"
          >
            {sales.recent.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No sales yet — keep going.</p>
            ) : (
              <ul className="divide-y divide-silver/15" data-testid="recent-sales-list">
                {sales.recent.slice(0, 6).map((s, i) => (
                  <li key={i} className="py-2.5 flex items-center justify-between text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="text-foreground truncate" title={s.customer_email || 'anonymous'}>
                        {s.customer_email || <span className="italic text-muted-foreground">anonymous checkout</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground">{fmtTimeAgo(s.created_at)}</div>
                    </div>
                    <div className="font-mono font-semibold text-secondary">{fmtMoney(s.amount)}</div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* EMAIL PIPELINE */}
          <Section
            icon={Mail}
            title="Email Pipeline"
            subtitle={`${email_pipeline.due_next_24h} scheduled to send in next 24h`}
            testid="section-email"
          >
            <div className="space-y-3">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">By status</p>
                <div className="flex flex-wrap gap-2" data-testid="email-by-status">
                  {Object.entries(email_pipeline.by_status).map(([status, count]) => (
                    <Badge
                      key={status}
                      className={`${STATUS_TONE[status] || STATUS_TONE.draft} border`}
                      variant="outline"
                    >
                      {status}: <span className="font-bold ml-1">{count}</span>
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">By campaign</p>
                <ul className="text-sm space-y-1" data-testid="email-by-kind">
                  {Object.entries(email_pipeline.by_kind).map(([kind, n]) => (
                    <li key={kind} className="flex justify-between text-foreground/90">
                      <span className="font-mono text-xs text-muted-foreground">{kind}</span>
                      <span className="font-semibold">{n}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Section>

          {/* PB QUEUE */}
          <Section
            icon={ListChecks}
            title="PB Content Queue"
            subtitle={`${pb_queue.total} posts staged across platforms`}
            testid="section-queue"
          >
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2" data-testid="queue-by-status">
                {Object.entries(pb_queue.by_status).map(([status, n]) => (
                  <Badge
                    key={status}
                    className={`${STATUS_TONE[status] || STATUS_TONE.draft} border`}
                    variant="outline"
                  >
                    {status}: <span className="font-bold ml-1">{n}</span>
                  </Badge>
                ))}
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">By platform</p>
                <ul className="text-sm space-y-1" data-testid="queue-by-platform">
                  {Object.entries(pb_queue.by_platform).map(([plat, n]) => (
                    <li key={plat} className="flex justify-between text-foreground/90">
                      <span className="text-muted-foreground">{plat}</span>
                      <span className="font-semibold">{n}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Section>

          {/* TODAY'S REACH (24h) */}
          <Section
            icon={Radio}
            title="Today's Reach"
            subtitle="Posts that actually went out in the last 24h"
            testid="section-todays-reach"
          >
            {(() => {
              const reach = content360.today_reach || { published_24h: 0, scheduled_24h: 0, failed_24h: 0, platforms: {}, recent_captions: [] };
              const totalLive = reach.published_24h + reach.scheduled_24h;
              if (totalLive === 0 && reach.failed_24h === 0) {
                return (
                  <p className="text-sm text-muted-foreground text-center py-6" data-testid="todays-reach-empty">
                    Nothing went out yet today. <span className="text-secondary">Open Content360 and publish a post — it&apos;ll show up here automatically.</span>
                  </p>
                );
              }
              const platformEntries = Object.entries(reach.platforms || {});
              return (
                <div className="space-y-4" data-testid="todays-reach-body">
                  {/* Stat row */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2.5">
                      <div className="text-[10px] uppercase tracking-widest text-emerald-400/80 font-semibold">Published</div>
                      <div className="text-2xl font-display font-bold text-emerald-400" data-testid="reach-published-count">{reach.published_24h}</div>
                    </div>
                    <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5">
                      <div className="text-[10px] uppercase tracking-widest text-amber-400/80 font-semibold">Scheduled</div>
                      <div className="text-2xl font-display font-bold text-amber-400" data-testid="reach-scheduled-count">{reach.scheduled_24h}</div>
                    </div>
                    <div className={`rounded-lg border px-3 py-2.5 ${reach.failed_24h > 0 ? 'border-red-500/30 bg-red-500/5' : 'border-silver/15 bg-card/30'}`}>
                      <div className={`text-[10px] uppercase tracking-widest font-semibold ${reach.failed_24h > 0 ? 'text-red-400/80' : 'text-muted-foreground'}`}>Failed</div>
                      <div className={`text-2xl font-display font-bold ${reach.failed_24h > 0 ? 'text-red-400' : 'text-foreground/60'}`} data-testid="reach-failed-count">{reach.failed_24h}</div>
                    </div>
                  </div>

                  {/* Platform pills */}
                  {platformEntries.length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="reach-platforms">
                      <span className="text-xs text-muted-foreground self-center mr-1">Platforms hit:</span>
                      {platformEntries.map(([plat, n]) => (
                        <Badge key={plat} variant="outline" className="capitalize border-secondary/30 text-secondary text-xs">
                          {plat} · {n}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Recent captions */}
                  {reach.recent_captions && reach.recent_captions.length > 0 && (
                    <div className="border-t border-silver/10 pt-3" data-testid="reach-captions">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Latest captions</p>
                      <ul className="space-y-1.5">
                        {reach.recent_captions.map((c, i) => (
                          <li key={`${c.received_at}-${i}`} className="text-xs text-foreground/80">
                            <span className="capitalize text-secondary font-semibold">{c.platform}</span>
                            <span className="text-muted-foreground/60"> · {fmtTimeAgo(c.received_at)} · </span>
                            <span className="line-clamp-1">{c.caption_preview}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })()}
          </Section>

          {/* CONTENT360 */}
          <Section
            icon={Globe}
            title="Content360 Activity"
            subtitle={`${content360.total_events} events · ${content360.events_24h} in last 24h`}
            testid="section-content360"
          >
            {content360.recent.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No events yet — set up the webhook in the PB Content360 tab.</p>
            ) : (
              <ul className="space-y-2" data-testid="content360-recent">
                {content360.recent.slice(0, 5).map((ev) => {
                  const isPub = ev.event_type?.includes('publish') && !ev.event_type?.includes('fail');
                  const isFail = ev.event_type?.includes('fail');
                  const Icon = isPub ? CheckCircle2 : isFail ? AlertCircle : Clock;
                  const tone = isPub ? 'text-emerald-400' : isFail ? 'text-red-400' : 'text-amber-400';
                  return (
                    <li key={ev.id} className="flex items-start gap-2 text-sm">
                      <Icon className={`w-3.5 h-3.5 mt-1 shrink-0 ${tone}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold capitalize text-foreground/90">
                            {ev.event_type?.replace(/_/g, ' ')}
                          </span>
                          {ev.platform && (
                            <Badge variant="outline" className="text-[10px] capitalize border-secondary/30 text-secondary">
                              {ev.platform}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-1">{ev.caption_preview}</p>
                        <p className="text-[10px] text-muted-foreground/70">{fmtTimeAgo(ev.received_at)}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>

          {/* CART RECOVERY */}
          <Section
            icon={Sparkles}
            title="Cart Recovery"
            subtitle="Abandoned vs recovered checkouts"
            testid="section-cart"
          >
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-4 rounded-lg bg-background/40 border border-silver/15">
                <div className="text-2xl font-heading text-foreground" data-testid="cart-abandoned">
                  {cart_recovery.abandoned_open}
                </div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mt-1">
                  Abandoned (open)
                </div>
              </div>
              <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                <div className="text-2xl font-heading text-emerald-400" data-testid="cart-recovered">
                  {cart_recovery.recovered_with_promo}
                </div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mt-1">
                  Recovered via promo
                </div>
              </div>
            </div>

            <CartRecoveryBlastBlock token={token} onSent={() => load()} />
          </Section>
        </div>
      </div>
    </div>
  );
}
